import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { buildEditorExtensions } from './editorExtensions.mjs';
import { imageInsertPos } from './markdownTableSupport.mjs';
import './ActionEditor.css';

const TOOLBAR_HALF_WIDTH = 160;
const SLASH_MENU_WIDTH = 232;

// What tiptap-markdown's html:false fallback writes when it cannot serialize
// a node. The table guards in markdownTableSupport.mjs should make this
// unreachable — the regex is the fail-loudly backstop at the save.
const SERIALIZER_BAIL_RE = /^\[(table|hardBreak)\]$/m;

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`toolbar-btn ${active ? 'toolbar-btn-active' : ''}`}
    >
      {children}
    </button>
  );
}

// Read a File into a data URL, downscaling large images so pasted Retina
// screenshots don't balloon the stored document. Keeps small files and GIFs
// untouched; everything else above the size threshold is re-encoded as JPEG
// capped at 1600px on the long edge.
function fileToDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

async function imageFileToSrc(file) {
  const MAX_EDGE = 1600;
  const PASSTHROUGH_BYTES = 250 * 1024;
  const isSvg = file.type === 'image/svg+xml';
  // SVG must be rasterized: the markdown parser only re-loads
  // data:image/png|jpeg|gif|webp URIs, so a raw SVG would vanish on reload.
  if (!isSvg && (file.type === 'image/gif' || file.size <= PASSTHROUGH_BYTES)) {
    return fileToDataUrl(file);
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new window.Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const w = img.naturalWidth || 800;
    const h = img.naturalHeight || 600;
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    if (isSvg) return canvas.toDataURL('image/png'); // keep transparency
    const jpeg = canvas.toDataURL('image/jpeg', 0.85);
    const original = await fileToDataUrl(file);
    return original && original.length < jpeg.length ? original : jpeg;
  } catch {
    return isSvg ? null : fileToDataUrl(file);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function imageFilesFrom(dataTransfer) {
  return Array.from(dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
}

// ── Slash-command menu ──
const SLASH_ITEMS = [
  { id: 'h2',      label: 'Heading',       hint: '##',  keywords: 'heading h2 title section',   command: (c) => c.setNode('heading', { level: 2 }) },
  { id: 'h3',      label: 'Subheading',    hint: '###', keywords: 'subheading h3 sub',          command: (c) => c.setNode('heading', { level: 3 }) },
  { id: 'bullet',  label: 'Bullet list',   hint: '-',   keywords: 'bullet list ul unordered',   command: (c) => c.toggleBulletList() },
  { id: 'ordered', label: 'Numbered list', hint: '1.',  keywords: 'numbered ordered list ol',   command: (c) => c.toggleOrderedList() },
  { id: 'task',    label: 'Task list',     hint: '[ ]', keywords: 'task todo checkbox check',   command: (c) => c.toggleTaskList() },
  { id: 'quote',   label: 'Quote',         hint: '>',   keywords: 'quote blockquote',           command: (c) => c.toggleBlockquote() },
  { id: 'code',    label: 'Code block',    hint: '```', keywords: 'code block snippet',         command: (c) => c.toggleCodeBlock() },
  { id: 'divider', label: 'Divider',       hint: '---', keywords: 'divider rule hr separator',  command: (c) => c.setHorizontalRule() },
  { id: 'image',   label: 'Image…',        hint: '',    keywords: 'image picture photo screenshot', command: null }, // opens file picker
];

function filterSlashItems(query) {
  if (!query) return SLASH_ITEMS;
  const q = query.toLowerCase();
  return SLASH_ITEMS.filter(i => i.label.toLowerCase().startsWith(q) || i.keywords.includes(q));
}

const ActionEditor = forwardRef(function ActionEditor({ content, onSave, onNavigate, onFocus }, ref) {
  const saveTimer = useRef(null);
  const wrapRef = useRef(null);
  const editorRefInternal = useRef(null);
  const onNavigateRef = useRef(null);
  onNavigateRef.current = onNavigate;
  const onFocusRef = useRef(null);
  onFocusRef.current = onFocus;
  const fileInputRef = useRef(null);
  const pendingSave = useRef(false);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  // What the outside world believes this note says. onUpdate compares against
  // it so that mount-time normalisation transactions never count as edits;
  // it moves forward when the prop changes and when we actually save.
  const contentRef = useRef(content || '');
  contentRef.current = content || '';
  const [toolbar, setToolbar] = useState({ visible: false, top: 0, left: 0 });
  const [wordCount, setWordCount] = useState(0);
  const [saveBlocked, setSaveBlocked] = useState(false);
  const [slash, setSlash] = useState(null); // { query, from, to, top, left, index }
  const slashRef = useRef(null);
  slashRef.current = slash;

  const countWords = (editor) => {
    const text = editor.state.doc.textBetween(0, editor.state.doc.content.size, ' ', ' ');
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    setWordCount(words);
  };

  const insertImages = useCallback((editor, files, pos = null) => {
    files.forEach(async (file) => {
      const src = await imageFileToSrc(file);
      if (!src || !editor) return;
      const chain = editor.chain().focus();
      const target = pos !== null ? pos : editor.state.selection.from;
      const lifted = imageInsertPos(editor.state, target);
      if (lifted !== null) chain.insertContentAt(lifted, { type: 'image', attrs: { src } }).run();
      else if (pos !== null) chain.insertContentAt(pos, { type: 'image', attrs: { src } }).run();
      else chain.setImage({ src }).run();
    });
  }, []);

  const applySlashItem = useCallback((item) => {
    const s = slashRef.current;
    const editor = editorRefInternal.current;
    if (!s || !editor) return;
    const chain = editor.chain().focus().deleteRange({ from: s.from, to: s.to });
    if (item.id === 'image') {
      chain.run();
      fileInputRef.current?.click();
    } else {
      item.command(chain).run();
    }
    setSlash(null);
  }, []);

  const checkSlash = useCallback((editor) => {
    const { selection } = editor.state;
    const { empty, $from } = selection;
    if (!empty || !$from.parent.isTextblock || $from.parent.type.name === 'codeBlock') { setSlash(null); return; }
    const textBefore = $from.parent.textBetween(0, $from.parentOffset, '\0', '\0');
    const m = /(?:^|\s)\/([a-zA-Z0-9]*)$/.exec(textBefore);
    if (!m || filterSlashItems(m[1]).length === 0) { setSlash(null); return; }
    if (!wrapRef.current) return;
    const coords = editor.view.coordsAtPos($from.pos);
    const wrap = wrapRef.current.getBoundingClientRect();
    setSlash(prev => ({
      query: m[1],
      from: $from.pos - m[1].length - 1,
      to: $from.pos,
      top: coords.bottom - wrap.top + 6,
      left: Math.max(0, Math.min(coords.left - wrap.left, wrap.width - SLASH_MENU_WIDTH)),
      index: prev && prev.query !== m[1] ? 0 : (prev?.index || 0),
    }));
  }, []);

  const editor = useEditor({
    extensions: buildEditorExtensions(),
    content: content || '',
    editorProps: {
      handlePaste: (view, event) => {
        const files = imageFilesFrom(event.clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();
        insertImages(editorRefInternal.current, files);
        return true;
      },
      handleDrop: (view, event) => {
        const files = imageFilesFrom(event.dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        insertImages(editorRefInternal.current, files, coords ? coords.pos : null);
        return true;
      },
      // ⌘-click opens links (Mac-native feel; plain click keeps editing)
      handleClick: (view, pos, event) => {
        if (!event.metaKey) return false;
        const link = event.target.closest?.('a[href]');
        if (link) { window.open(link.href, '_blank', 'noopener'); return true; }
        return false;
      },
      // Keyboard navigation for the slash menu + boundary flow between actions
      handleKeyDown: (view, event) => {
        const s = slashRef.current;
        if (!s) {
          // Boundary flow (zen): ↓ with the caret already at the very end of
          // the note flows to the next action; ↑ at the very start flows to
          // the previous. Mid-note, arrows edit text like anywhere else.
          if (onNavigateRef.current && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey) {
            const { selection, doc } = view.state;
            if (selection.empty) {
              if (event.key === 'ArrowDown' && selection.from >= doc.content.size - 1) {
                onNavigateRef.current(1);
                return true;
              }
              if (event.key === 'ArrowUp' && selection.from <= 1) {
                onNavigateRef.current(-1);
                return true;
              }
            }
          }
          return false;
        }
        const items = filterSlashItems(s.query);
        if (event.key === 'ArrowDown') {
          setSlash({ ...s, index: (s.index + 1) % items.length });
          return true;
        }
        if (event.key === 'ArrowUp') {
          setSlash({ ...s, index: (s.index - 1 + items.length) % items.length });
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          applySlashItem(items[Math.min(s.index, items.length - 1)]);
          return true;
        }
        if (event.key === 'Escape') {
          event.stopPropagation(); // just dismiss the menu — don't close the pane
          setSlash(null);
          return true;
        }
        return false;
      },
    },
    onCreate: ({ editor }) => countWords(editor),
    onUpdate: ({ editor }) => {
      countWords(editor);
      // Reading writes nothing — enforced, not assumed. Mounting a note can
      // fire update transactions (content parsing, markdown normalisation)
      // that carry no keystroke; saving those would stamp lastTouchedAt on an
      // action the user only READ, and the forged "last worked" would then lie
      // in the Rounds and on the shelf. Only a doc that differs from what was
      // handed in is worth saving. (UX-2, Aug 2026 review.)
      const md = editor.storage.markdown.getMarkdown();
      if (md === (contentRef.current || '')) return;
      // Fail loudly, never silently: a placeholder token in the markdown
      // means the serializer bailed and the note would save with real content
      // replaced by "[table]". Refuse the save instead of destroying it — the
      // editor still holds the doc, and the stored copy keeps the last good
      // version. Any clean edit resumes saving.
      if (SERIALIZER_BAIL_RE.test(md)) {
        clearTimeout(saveTimer.current);
        pendingSave.current = false;
        setSaveBlocked(true);
        return;
      }
      setSaveBlocked(false);
      pendingSave.current = true;
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        pendingSave.current = false;
        onSaveRef.current(editor.storage.markdown.getMarkdown());
      }, 600);
    },
  });

  editorRefInternal.current = editor;

  useImperativeHandle(ref, () => ({
    getMarkdown: () => editor ? editor.storage.markdown.getMarkdown() : '',
    getHTML: () => editor ? editor.getHTML() : '',
    isEmpty: () => editor ? editor.isEmpty : true,
    focus: (where = 'end') => { editor?.commands.focus(where); },
  }), [editor]);

  // Position floating toolbar on selection
  const updateToolbar = useCallback(() => {
    if (!editor || !wrapRef.current) return;
    const { from, to } = editor.state.selection;
    if (from === to) { setToolbar(t => ({ ...t, visible: false })); return; }

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setToolbar(t => ({ ...t, visible: false })); return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const wrap = wrapRef.current.getBoundingClientRect();

    setToolbar({
      visible: true,
      top: rect.top - wrap.top - 44,
      left: Math.max(0, Math.min(
        wrap.width - TOOLBAR_HALF_WIDTH * 2,
        rect.left - wrap.left + rect.width / 2 - TOOLBAR_HALF_WIDTH
      )),
    });
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const onTransaction = () => { updateToolbar(); checkSlash(editor); };
    editor.on('selectionUpdate', onTransaction);
    editor.on('transaction', onTransaction);
    editor.on('blur', () => setTimeout(() => setSlash(null), 150));
    return () => {
      editor.off('selectionUpdate', onTransaction);
      editor.off('transaction', onTransaction);
    };
  }, [editor, updateToolbar, checkSlash]);

  // Sync externally-changed content into the editor. Action switching is
  // handled by the key={action.id} remount in DetailPane, so this only fires
  // for out-of-band updates to the open action's notes.
  // NEVER while the editor is focused: the debounced save echoes `content`
  // back down, and if a keystroke lands before that echo commits, setContent
  // would replace the doc with the stale string — deleting the keystroke and
  // throwing the caret to the end of the note. While typing, the editor is
  // the source of truth.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const current = editor.storage.markdown.getMarkdown();
    if (current !== (content || '')) {
      editor.commands.setContent(content || '');
      countWords(editor);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // On unmount (pane closed / action switched): flush any pending save so the
  // last ≤600ms of typing is never lost. Same bail guard as onUpdate — a
  // poisoned flush would overwrite the stored note with "[table]".
  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    if (pendingSave.current && editorRefInternal.current) {
      pendingSave.current = false;
      const md = editorRefInternal.current.storage.markdown.getMarkdown();
      if (!SERIALIZER_BAIL_RE.test(md)) onSaveRef.current(md);
    }
  }, []);

  const setLinkFromClipboard = async () => {
    if (!editor) return;
    if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
    try {
      const text = (await navigator.clipboard.readText()).trim();
      const url = /^https?:\/\//i.test(text) ? text
        : /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(text) ? `https://${text}` : null;
      if (url) editor.chain().focus().setLink({ href: url }).run();
    } catch { /* clipboard permission denied — paste the URL over the selection instead */ }
  };

  const slashItems = slash ? filterSlashItems(slash.query) : [];

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} onFocus={() => onFocusRef.current?.()}>
      {toolbar.visible && editor && (
        <div
          className="floating-toolbar"
          style={{ top: toolbar.top, left: toolbar.left }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (⌘B)">
            <b>B</b>
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (⌘I)">
            <i>I</i>
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough (⌘⇧S)">
            <s>S</s>
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Code (⌘E)">
            {'</>'}
          </ToolbarBtn>
          <span className="toolbar-divider" />
          <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading (## + space)">
            H2
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Subheading (### + space)">
            H3
          </ToolbarBtn>
          <span className="toolbar-divider" />
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list (- + space)">
            •
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list (1. + space)">
            1.
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title="Task list ([ ] + space)">
            ☑
          </ToolbarBtn>
          <span className="toolbar-divider" />
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote (> + space)">
            ❝
          </ToolbarBtn>
          <ToolbarBtn onClick={setLinkFromClipboard} active={editor.isActive('link')} title="Link — copies URL from clipboard; or just paste a URL over selected text">
            ⌁
          </ToolbarBtn>
        </div>
      )}
      {slash && slashItems.length > 0 && (
        <div className="slash-menu" style={{ top: slash.top, left: slash.left }} onMouseDown={(e) => e.preventDefault()}>
          {slashItems.map((item, i) => (
            <button
              key={item.id}
              className={`slash-item ${i === Math.min(slash.index, slashItems.length - 1) ? 'slash-item-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); applySlashItem(item); }}
              onMouseEnter={() => setSlash(s => s ? { ...s, index: i } : s)}
            >
              <span className="slash-label">{item.label}</span>
              {item.hint && <span className="slash-hint">{item.hint}</span>}
            </button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} className="action-editor" />
      {saveBlocked && (
        <div className="editor-save-blocked" role="alert">
          A table in this note can't be saved faithfully, so saving is paused to protect it.
          Copy your text somewhere safe and simplify the table — no headings or multiple
          paragraphs inside cells — and saving resumes on the next edit.
        </div>
      )}
      {wordCount > 0 && (
        <div className="editor-word-count">{wordCount.toLocaleString()} {wordCount === 1 ? 'word' : 'words'}</div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
          if (files.length) insertImages(editorRefInternal.current, files);
          e.target.value = '';
        }}
      />
    </div>
  );
});

export default ActionEditor;
